import React from 'react';
import { FancyLoader } from './FancyLoader';
import './shimmer.css';

interface SkeletonProps {
  className?: string;
  children?: React.ReactNode;
  loaderSizePx?: number;
  loaderClassName?: string;
  noLoader?: boolean;
}

const Skeleton: React.FC<SkeletonProps> = ({
  className = '',
  children,
  loaderSizePx = 28,
  loaderClassName = 'text-slate-500/80 dark:text-slate-300/70',
  noLoader = false
}) => {
  return (
    <div className={`relative bg-slate-100 dark:bg-slate-800/50 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700/50 ${className}`}>
      {/* Shimmer Overlay */}
      <div className="absolute inset-0 shimmer opacity-50" />

      {children}
      {!noLoader && (
        <div className={`absolute inset-0 flex items-center justify-center z-10 ${loaderClassName}`}>
          <FancyLoader label={null} sizePx={loaderSizePx} />
        </div>
      )}
    </div>
  );
};

// Predefined skeleton variants
export const SkeletonCard: React.FC<{ className?: string }> = ({ className = '' }) => (
  <Skeleton className={`h-32 ${className}`} noLoader />
);

export const SkeletonRow: React.FC<{ className?: string }> = ({ className = '' }) => (
  <Skeleton className={`h-12 ${className}`} noLoader />
);

export const SkeletonHeading: React.FC<{ className?: string }> = ({ className = '' }) => (
  <Skeleton className={`h-8 w-64 mb-4 ${className}`} noLoader />
);

export const SkeletonText: React.FC<{ lines?: number; className?: string }> = ({
  lines = 1,
  className = ''
}) => (
  <div className={`space-y-2 ${className}`}>
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton
        key={i}
        className={`h-4 ${i === lines - 1 ? 'w-3/4' : 'w-full'}`}
        noLoader
      />
    ))}
  </div>
);

export const SkeletonChart: React.FC<{ className?: string }> = ({ className = '' }) => (
  <Skeleton
    className={`h-72 ${className}`}
    loaderSizePx={44}
    loaderClassName="text-emerald-500/20 dark:text-emerald-400/10"
  />
);

export const SkeletonMetricCard: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`p-6 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white/50 dark:bg-slate-900/50 shadow-sm ${className}`}>
    <div className="space-y-4">
      {/* Title */}
      <Skeleton className="h-4 w-24" noLoader />
      {/* Value */}
      <Skeleton className="h-10 w-28" noLoader />
      {/* Delta chip */}
      <Skeleton className="h-6 w-16 rounded-full" noLoader />
      {/* Sparkline */}
      <div className="pt-2">
        <Skeleton className="h-12 w-full mx-auto opacity-50" noLoader />
      </div>
    </div>
  </div>
);

export const SkeletonKpiGrid: React.FC<{ count?: number; className?: string }> = ({
  count = 6,
  className = ''
}) => (
  <div className={`grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 ${className}`}>
    {Array.from({ length: count }).map((_, i) => (
      <SkeletonMetricCard key={i} />
    ))}
  </div>
);

export const SkeletonSection: React.FC<{ title?: boolean; className?: string }> = ({
  title = true,
  className = ''
}) => (
  <div className={`space-y-6 ${className}`}>
    {title && <SkeletonHeading />}
    <SkeletonKpiGrid count={4} />
  </div>
);

export const DashboardLoadingSkeleton: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`min-h-screen bg-slate-50 dark:bg-slate-950/20 ${className}`}>
    <main className="mx-auto max-w-[1400px] p-4 md:p-8">
      <div className="space-y-12">
        {/* Header Skeleton */}
        <div className="space-y-3">
          <Skeleton className="h-10 w-48" noLoader />
          <Skeleton className="h-4 w-32" noLoader />
        </div>

        {/* Hero Section Skeleton */}
        <div className="space-y-6">
          <SkeletonHeading />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <SkeletonMetricCard />
            <SkeletonMetricCard />
            <SkeletonMetricCard />
            <SkeletonMetricCard />
          </div>
        </div>

        {/* Chart Section Skeleton */}
        <div className="grid lg:grid-cols-3 gap-6">
          <SkeletonChart className="lg:col-span-2" />
          <div className="space-y-4">
            <SkeletonCard />
            <SkeletonCard />
          </div>
        </div>
      </div>
    </main>
  </div>
);

export default Skeleton;